// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * The `register_surface` a pack form template declares to say "my
 * `legal_basis` field's options are the bases this instrument recognises".
 *
 * Declared once, here, so no module spells it (AC1). The template is read
 * through `readPackFormsBySurface` + `packFormOptions` — the reader 62.24.0
 * already shipped (REUSE.md §9) — so this sprint adds no installer field, no
 * collection and no install-path migration.
 */
export const LAWFUL_BASIS_REGISTER_SURFACE = 'lawful_basis_vocabulary';
/**
 * The `register_surface` a pack declares to say, against the signed text, that
 * its instrument enumerates NO lawful basis.
 *
 * A separate surface rather than an empty option list, because an empty list
 * and an absent declaration are the same shape and must not read the same way:
 * one is a verified negative and the other is an unanswered question (LD5).
 * Moved here from the backend vocabulary service by fix-sprint-20261003-0625
 * F11, so the pack-locked surface set below can name it.
 */
export const LAWFUL_BASIS_VERIFIED_NONE_SURFACE = 'lawful_basis_declares_none';
/**
 * fix-sprint-20261003-0625 F11 — the two declaration surfaces. A template on
 * either is a pack's statement of what its instrument declares, read by the
 * vocabulary reader and re-installed by the boot declaration sync. It is not
 * an organisation's to edit: every Compliance Forms mutation path (update,
 * uninstall, clone) refuses it with 409 `TEMPLATE_REGISTER_LOCKED`
 * (`PACK_LOCKED_REGISTER_SURFACES`), so an admin edit can no longer be
 * silently reverted by the next boot — there is no edit to revert.
 */
export const LAWFUL_BASIS_DECLARATION_SURFACES = [
    LAWFUL_BASIS_REGISTER_SURFACE,
    LAWFUL_BASIS_VERIFIED_NONE_SURFACE,
];
/** The field id whose `options[]` carry the declared bases. */
export const LAWFUL_BASIS_VOCABULARY_FIELD = 'legal_basis';
/**
 * The article this basis rests on, as authored on the option
 * (`Điều 19 khoản 1 điểm d`, `Art. 6(1)(b)`).
 *
 * **Required.** An option with no citation is dropped by the reader and failed
 * by the pack validator. This is the key finding of the verification pass made
 * non-optional: `vn_mau_01a_qd778` shipped five uncited options attributed to
 * an instrument whose signed text contains no lawful-basis field at all.
 */
export const PACK_OPTION_BASIS_CITATION = 'citation';
/**
 * The signed source the citation was verified against, as recorded at intake
 * (a file name under the instrument's `source/` directory). **Required.**
 *
 * A citation with no source names an article nobody opened.
 */
export const PACK_OPTION_BASIS_CITATION_SOURCE = 'citation_verified_against';
/**
 * Value spellings a stored record may already carry for this same basis.
 * Optional, and it exists for one measured reason rather than speculatively:
 * the EU pack spells three of its six `vital_interests`, `public_task` and
 * `legitimate_interests` while every stored record spells them
 * `vital_interest`, `public_interest` and `legitimate_interest`. Left
 * unreconciled, the migration would queue every EU tenant's records for
 * re-determination — a regression dressed as diligence.
 *
 * Resolution is read-time and **no stored document is rewritten**.
 */
export const PACK_OPTION_BASIS_SUPERSEDED_VALUES = 'superseded_values';
/**
 * 62.27.0 gate E12.5 pass 2, OD38 — has ANY instrument of this vocabulary been
 * measured? True when at least one instrument in the measurement (OD43a: the
 * instruments of declaring stacks) has a known declaration state
 * (`declares_bases` or `verified_declares_none`). An org whose every
 * instrument is `declaration_not_verified`, or that has none, has not been
 * measured: its empty option list is an unanswered question, not a verified
 * "declares nothing" (LD5).
 *
 * Declared here, in `shared`, since fix-sprint-20261003-0625 F10: the pages
 * and the server exports must ask the same question, and before this the
 * frontend had no copy of it and passed `options: []` where the exports
 * passed `null`, so the page and the export disagreed about one record.
 */
export function vocabularyIsMeasured(vocabulary) {
    return vocabulary.instruments.some((i) => i.state !== 'declaration_not_verified');
}
/**
 * fix-sprint-20261003-0625 F13 (owner ruling OD43, amended by OD43a) — has
 * EVERY instrument in the measurement answered? A mixed stack is measured per
 * instrument: while any instrument of a declaring stack is
 * `declaration_not_verified`, a stored code that no measured instrument
 * declares might still be declared by the one that has not answered, so it is
 * left unannotated rather than queued. Only once every one of them has
 * answered may "nothing declares it" be said — by the write rule, the
 * annotation, the OD41 re-grade, the boot migration and every list surface
 * alike.
 *
 * OD43a: "every instrument" is every instrument of a DECLARING stack, and
 * OD43b: a stack is declaring when the row's OWN code has answered. The
 * reader lists only those in `instruments`, so a framework installed beside
 * them whose stack declares nothing for this surface (a management standard,
 * a law with no lawful-basis article) is outside the measurement and blocks
 * nothing. An org with no declaring stack has answered nothing (OD38): its
 * `instruments` is empty and this is `false`.
 */
export function vocabularyIsFullyMeasured(vocabulary) {
    return (vocabulary.instruments.length > 0 &&
        vocabulary.instruments.every((i) => i.state !== 'declaration_not_verified'));
}
/**
 * Upper bounds on a person's own ground and its article, enforced at the
 * boundary of every writer that accepts them. Declared once so the five
 * schemas and the form cannot disagree about how long a ground may be.
 */
export const LAWFUL_BASIS_STATEMENT_MAX_LENGTH = 2000;
export const LAWFUL_BASIS_CITATION_MAX_LENGTH = 500;
/**
 * The text to render for a record's lawful basis in a table cell, a badge or
 * an export column.
 *
 * A record a person resolved in their own words renders those words — an
 * empty cell would say the basis is missing when it is documented, which is
 * the opposite of the truth. Every other record renders through
 * `lawfulBasisLabel`, so a declared code reads as its declared label and an
 * undeclared one reads as itself. As with `lawfulBasisLabel`, the citation is
 * not appended: in a register it would repeat on every row.
 */
export function recordedLawfulBasisLabel(options, record, locale) {
    if (record.legal_basis_resolution === 'recorded_by_person') {
        const statement = record.legal_basis_statement?.trim() ?? '';
        if (statement.length > 0)
            return statement;
    }
    return lawfulBasisLabel(options, record.legal_basis, locale);
}
export function recordedLawfulBasisDisplay(options, record, locale, 
/**
 * fix-sprint-20261003-0625 F13 (OD43, OD43a) — may an unstamped code that
 * no option declares be called undeclared? Only when EVERY instrument of
 * every declaring stack has answered (`vocabularyIsFullyMeasured`); a
 * declaring stack still waiting on one instrument shows the code as stored,
 * and keeps the labels of the codes its measured instruments do declare.
 * Defaults to "the vocabulary has answered" (`options !== null`), the
 * behaviour before OD43.
 */
gradesUndeclared = options !== null) {
    if (record.legal_basis_resolution === 'recorded_by_person') {
        const statement = record.legal_basis_statement?.trim() ?? '';
        if (statement.length > 0)
            return { state: 'label', text: statement };
    }
    const code = typeof record.legal_basis === 'string' ? record.legal_basis.trim() : '';
    if (code.length === 0)
        return { state: 'label', text: '' };
    if (record.legal_basis_resolution === 'needs_redetermination') {
        return { state: 'needs_redetermination', code };
    }
    if (gradesUndeclared && options !== null && findLawfulBasisOption(options, code) === null) {
        return { state: 'needs_redetermination', code };
    }
    return { state: 'label', text: lawfulBasisLabel(options ?? [], code, locale) };
}
export function lawfulBasisListInput(vocabulary) {
    if (!vocabulary || !vocabularyIsMeasured(vocabulary)) {
        return { options: null, gradesUndeclared: false };
    }
    return { options: vocabulary.options, gradesUndeclared: vocabularyIsFullyMeasured(vocabulary) };
}
/**
 * `recordedLawfulBasisDisplay` as one string, for a surface that holds text
 * only (an export column, a read-only input). `needsRedetermination` is the
 * caller's translated sentence with a `{basis}` slot, so this file holds no
 * i18n key and the stored code is named verbatim.
 */
export function recordedLawfulBasisText(options, record, locale, needsRedetermination, 
/** As `recordedLawfulBasisDisplay` (OD43). */
gradesUndeclared = options !== null) {
    const display = recordedLawfulBasisDisplay(options, record, locale, gradesUndeclared);
    // 62.27.0 gate E12.5 pass 2, P2-4 — a FUNCTION replacement. A string
    // replacement expands `$&`, `$'`, `` $` `` and `$$` in the replacement, so a
    // stored code (it is free text from before the vocabulary) reading `$&`
    // printed the placeholder itself, and `$'` printed the rest of the sentence.
    return display.state === 'label'
        ? display.text
        : needsRedetermination.replace('{basis}', () => display.code);
}
/**
 * Whether a record carries a lawful basis at all: a stored code, or a ground a
 * person recorded in their own words WITH the article it rests on.
 *
 * For a reader that asks only "is a basis recorded" (the consent gap
 * analysis). It says nothing about whether a stored code is still declared —
 * that is `basisRequiresRedetermination`. The ROPA completeness rules do not
 * call this: they inline the same comparison so the prose oracle can see every
 * field they read (62.27.0 D4).
 */
export function lawfulBasisIsRecorded(record) {
    if (record.legal_basis_resolution === 'recorded_by_person') {
        return ((record.legal_basis_statement?.trim().length ?? 0) > 0 &&
            (record.legal_basis_citation?.trim().length ?? 0) > 0);
    }
    return typeof record.legal_basis === 'string' && record.legal_basis.trim().length > 0;
}
/**
 * Whether a recorded basis needs a person to determine it again.
 *
 * The single predicate the three ROPA completeness rule families share, so a
 * fourth copy of the question cannot appear (the `legalBasisMissing` pattern).
 */
export function basisRequiresRedetermination(basis) {
    return basis?.resolution === 'needs_redetermination';
}
/**
 * The declared option a stored code resolves to, and whether it answered under
 * a superseded spelling.
 *
 * **This is the one lookup.** `resolveStoredBasis` grades a record with it at
 * write time and every surface renders a label with it at read time, so the
 * question *"which declared basis is this stored code?"* has a single answer
 * in the product. Before this existed the write path did the search here and
 * five display sites did a different one — an i18n table keyed
 * `basis_<code>` — and the two disagreed for precisely the codes this sprint
 * added: `basis_urgent_protection_of_person` is not a key any bundle
 * authors, so a Vietnamese record rendered its raw stored code while the
 * control beside it offered the Vietnamese sentence.
 */
export function findLawfulBasisOption(options, code) {
    if (typeof code !== 'string' || code.trim().length === 0)
        return null;
    const wanted = code.trim();
    const declared = options.find((option) => option.value === wanted);
    if (declared)
        return { option: declared, via_superseded_value: false };
    const aliased = options.find((option) => option.superseded_values.includes(wanted));
    if (aliased)
        return { option: aliased, via_superseded_value: true };
    return null;
}
/**
 * The label to render for a stored lawful-basis code.
 *
 * Returns the stored code unchanged when no installed instrument declares it.
 * That is deliberate and is not a fallback label: the code is what the person
 * actually recorded, and inventing a friendlier noun for a ground the stack no
 * longer recognises would hide exactly the state
 * `needs_redetermination` exists to surface. The surfaces that must SAY so —
 * naming the superseded value, the sentence for a stack declaring nothing —
 * are `LawfulBasisField` on the five forms (work-file item O1). A table cell
 * rendering a whole record goes through `recordedLawfulBasisLabel`, so a
 * ground a person recorded in their own words renders those words.
 *
 * `citation` is deliberately not appended. A citation belongs beside a basis
 * the person is CHOOSING, where it is the evidence Decree 330 Điều 47 asks
 * for; in a table cell it would repeat on every row of a register.
 */
export function lawfulBasisLabel(options, code, locale) {
    if (typeof code !== 'string' || code.trim().length === 0)
        return '';
    const found = findLawfulBasisOption(options, code);
    if (!found)
        return code;
    const label = prefersVietnamese(locale) ? found.option.label.vi : found.option.label.en;
    return label.trim().length > 0 ? label : code;
}
/**
 * Whether a locale asks for the Vietnamese half of a `BilingualText`.
 *
 * `locale` is typed `string` rather than a two-member union because the two
 * callers spell it differently and always have: the admin pages hold `'vi'`
 * and the export renderer is handed `'vi-VN'`. A union would have forced a
 * cast at four call sites, and a cast is how the wrong one gets through.
 * Matching the LANGUAGE SUBTAG is one rule that is correct for both.
 *
 * Anything that is not Vietnamese reads English. That is the same fallback the
 * pages already applied as `locale === 'vi' ? label.vi : label.en`, not a new
 * policy — R-57 defers the other fourteen locales to Phase 25, and a pack
 * authors `BilingualText`, so there is no third string to choose.
 */
function prefersVietnamese(locale) {
    return locale.split('-')[0]?.toLowerCase() === 'vi';
}
export function lawfulBasisDeclarationDivergence(options, record) {
    if (options === null)
        return null;
    if (record.legal_basis_resolution !== 'pack_declared' &&
        record.legal_basis_resolution !== 'pack_declared_via_superseded_value') {
        return null;
    }
    const recordedBy = record.legal_basis_declared_by?.trim() ?? '';
    const recordedVersion = record.legal_basis_declared_version?.trim() ?? '';
    if (recordedBy.length === 0 || recordedVersion.length === 0)
        return null;
    const found = findLawfulBasisOption(options, record.legal_basis);
    if (!found) {
        return {
            kind: 'withdrawn',
            recorded_framework_code: recordedBy,
            recorded_version: recordedVersion,
        };
    }
    const installedVersion = found.option.declared_by_pack_version ?? null;
    if (found.option.declared_by_framework_code === recordedBy &&
        installedVersion === recordedVersion) {
        return null;
    }
    return {
        kind: 'changed',
        recorded_framework_code: recordedBy,
        recorded_version: recordedVersion,
        installed_framework_code: found.option.declared_by_framework_code,
        installed_version: installedVersion,
    };
}
//# sourceMappingURL=lawful-basis.js.map